import { TermsOfUseApi } from '@/features/account/api/tou-api'
import Env from '@/infra/env'
import { TermsOfUseStore } from '@/features/account/terms/TermsOfUseStore'
import { type Id } from '@/infra/id'
import { type TermsOfUseConfig } from '@/features/account/terms/terms-of-use-config'
import { TermsOfUseAcceptanceDialogViewModel } from '@/features/account/terms/TermsOfUseAcceptanceDialog/TermsOfUseAcceptanceDialogViewModel'
import { Section7216Api } from '@/features/account/api/section-7216-api'
import { Section7216Store } from '@/features/account/section-7216/Section7216Store'
import { Section7216AcceptanceDialogViewModel } from '@/features/account/section-7216/Section7216AcceptanceDialog/Section7216AcceptanceDialogViewModel'
import {
  type AccountFeatureOptions,
  type EnsureSection7216AcceptanceFn,
  type EnsureSection7216AcceptanceOptions,
  type EnsureTermsAcceptanceFn,
  type FetchTermsAcceptanceInfoFn,
} from '@/features/account'

/**
 * Function for getting the ToS configuration for a workspace ID.
 */
export type GetTermsOfUseConfigFn = (workspaceId: Id) => TermsOfUseConfig

export function InternalAccountFeature(options: AccountFeatureOptions) {
  const termsOfUseApi = new TermsOfUseApi({
    token$: options.token$,
    baseURL: Env.IAM_API,
  })

  const section7216Api = new Section7216Api({
    token$: options.token$,
    baseURL: Env.IAM_API,
  })

  const getConfig: GetTermsOfUseConfigFn = (workspaceId: Id) =>
    getTermsConfig(options, workspaceId)

  const termsOfUseStore = new TermsOfUseStore(getConfig, termsOfUseApi)
  const section7216Store = new Section7216Store(section7216Api)

  const termsOfUseAcceptanceVm = new TermsOfUseAcceptanceDialogViewModel(
    options.createSnackbarErrorHandler,
    termsOfUseStore,
    getConfig,
  )

  const section7216AcceptanceVm = new Section7216AcceptanceDialogViewModel(
    options.createSnackbarErrorHandler,
    section7216Store,
  )

  const ensureTermsAcceptance: EnsureTermsAcceptanceFn = (
    workspaceId: Id,
    signal?: AbortSignal,
  ) => termsOfUseAcceptanceVm.prompt(workspaceId, signal)

  const fetchTermsAcceptance: FetchTermsAcceptanceInfoFn = (
    workspaceId: Id,
    signal?: AbortSignal,
  ) => termsOfUseStore.fetchTermsAcceptance(workspaceId, signal)

  const ensureSection7216Acceptance: EnsureSection7216AcceptanceFn = (
    options: EnsureSection7216AcceptanceOptions,
  ) =>
    section7216AcceptanceVm.prompt({
      isSpouseSignatureRequired: options.isSpouseRequired,
      signal: options.signal,
    })

  return {
    // Internal
    getCurrentUser: options.getCurrentUser,
    termsOfUseStore,
    termsOfUseAcceptanceVm,
    section7216Store,
    section7216AcceptanceVm,

    // Public
    ensureTermsAcceptance,
    fetchTermsAcceptance,
    ensureSection7216Acceptance,
  }
}

/**
 * Gets terms of use configuration for the given workspace ID.
 *
 * @param options
 * @param workspaceId
 */
function getTermsConfig(
  options: AccountFeatureOptions,
  workspaceId: Id,
): TermsOfUseConfig {
  const workspace = options.getWorkspace(workspaceId)
  if (!workspace.config.termsOfService.enabled) {
    return { enabled: false }
  }

  return {
    enabled: true,
    customTerms: workspace.config.termsOfService.customTermsEnabled,
  }
}
